/* CotizadorModal.jsx — cotizador en línea de 4 pasos (port de hero-v3.jsx)
 * Auto → Plan → Seguro → Solicitud + pantalla de confirmación "¡Listo!".
 *
 * Dos modos (Fase 3):
 *  - real (prop `real` presente, modo manual del hero): aseguradoras REALES de
 *    RODAC con prima, preseleccionada la más barata; cambiarla re-cotiza la
 *    renta; "Enviar solicitud" → POST /updateBudget (type=1, manda mail).
 *  - mock (sin `real`, modo IA hasta F4): lógica del handoff tal cual.
 *
 * Props: { state, onClose, monthly?, real? }
 *   state   → shape de useHeroState().state
 *   monthly → renta real del backend (fallback si no hay `real`)
 *   real    → { options, selectedLogo, monthly, loading, setInsurance, submit }
 */
import { useState, useEffect } from 'react';
import { fmtMXN, calcMonthly, Ico } from '../../core/heroCore';
import { cap, planLabel } from './ContinueModal';
import { waLink } from '../../config';

export const SEGUROS = [
  { id: 'gnp', name: 'GNP', note: 'Cobertura amplia' },
  { id: 'hdi', name: 'HDI', note: 'Mejor precio' },
  { id: 'qualitas', name: 'Quálitas', note: 'Más talleres' },
  { id: 'chubb', name: 'Chubb', note: 'Premium' },
];

function prefillToForm(prefill) {
  if (!prefill) return { nombre: '', email: '', tel: '' };
  const isEmail = prefill.contacto && prefill.contacto.includes('@');
  return {
    nombre: prefill.nombre || '',
    email: isEmail ? prefill.contacto : '',
    tel: !isEmail && prefill.contacto ? prefill.contacto : '',
  };
}

export function Cotizador({ state, onClose, monthly: monthlyOverride = null, real = null, prefill = null }) {
  const [step, setStep] = useState(0);
  // real: el seguro llega PRESELECCIONADO (la opción más barata ya elegida en el strip)
  const [seguro, setSeguro] = useState(real ? (real.selectedLogo || '') : '');
  const [form, setForm] = useState(() => prefillToForm(prefill));
  const [sent, setSent] = useState(false);
  // Cierre con animación de salida: marca .closing, espera el fade y desmonta.
  const [closing, setClosing] = useState(false);
  const close = () => { setClosing(true); setTimeout(onClose, 200); };
  const [sending, setSending] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const steps = ['Auto', 'Plan', 'Seguro', 'Solicitud'];
  const monthly = real ? real.monthly : (monthlyOverride ?? calcMonthly(state));
  const busy = !!real?.loading;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // si la re-cotización cambió la selección efectiva (p.ej. fallback), sincroniza
  useEffect(() => {
    if (real?.selectedLogo) setSeguro(real.selectedLogo);
  }, [real?.selectedLogo]);

  // validación del paso de datos: correo con formato real y teléfono de 10 dígitos
  const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const telDigits = form.tel.replace(/\D/g, '');
  const telOk = telDigits.length === 10;
  const nombreOk = form.nombre.trim().length >= 2;

  const canNext =
    step === 2 ? (!!seguro && !busy) :
      step === 3 ? (nombreOk && emailOk && telOk && !sending && !busy) : true;

  const insurerLabel = real
    ? (real.options.find(o => o.logo === seguro)?.name || '—')
    : ((SEGUROS.find(x => x.id === seguro) || {}).name || '—');

  const next = async () => {
    if (step < 3) { setStep(step + 1); return; }
    if (!canNext) return;
    if (!real) { setSent(true); return; } // mock (modo IA): comportamiento del handoff
    setSending(true); setSubmitError(null);
    try {
      await real.submit({ nombre: form.nombre.trim(), email: form.email.trim(), tel: form.tel.trim() });
      setSent(true);
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className={`modal-scrim${closing ? ' closing' : ''}`} onClick={close}>
        <div className={`cz${closing ? ' closing' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
          <div className="cz-done">
            <div className="cz-done-ck"><Ico.Check /></div>
            <h3>¡Listo, {form.nombre.split(' ')[0] || 'crack'}!</h3>
            <p>Recibimos tu solicitud para el <b>{state.marca} {state.modelo} {state.version}</b>. {real
              ? 'Te enviamos un correo con tu cotización y los siguientes pasos — revísalo (checa también la carpeta de spam).'
              : 'En breve te enviamos tu cotización por correo.'} ¿Tienes dudas? <a
                href={waLink(
                  `Hola, soy ${form.nombre}. Acabo de solicitar mi cotización en Peicar y tengo una duda.\n\n`
                  + `Auto: ${state.marca} ${state.modelo} ${state.version}\n`
                  + `Plan: ${planLabel(state.plan)} · ${state.plazo} meses\n`
                  + `Pago inicial: ${fmtMXN(state.enganche)}\n`
                  + `Renta: ${fmtMXN(monthly)}/mes\n`
                  + `Seguro: ${insurerLabel}\n`
                  + `Correo: ${form.email}`
                )}
                target="_blank" rel="noopener noreferrer"
                style={{ color: 'inherit', textDecoration: 'underline', fontWeight: 600 }}
              >Escríbenos por WhatsApp</a>.</p>
            <div className="cz-summary cz-done-summary">
              <div><span>Cliente</span><b>{form.nombre}</b></div>
              <div><span>Correo</span><b>{form.email}</b></div>
              {form.tel && <div><span>Teléfono</span><b>{form.tel}</b></div>}
              <div><span>Auto</span><b>{state.marca} {state.modelo}</b></div>
              <div><span>Plan</span><b>{planLabel(state.plan)} · {state.plazo} meses</b></div>
              <div><span>Pago inicial</span><b>{fmtMXN(state.enganche)}</b></div>
              <div className="hl"><span>Renta mensual</span><b>{fmtMXN(monthly)}/mes</b></div>
              <div><span>Seguro</span><b>{insurerLabel}</b></div>
            </div>
            <p className="cz-disclaimer">Cotización de ejemplo · la cotización real puede cambiar.</p>
            <button className="cgo wide" onClick={close}>Cerrar <Ico.Check /></button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`modal-scrim${closing ? ' closing' : ''}`} onClick={close}>
      <div className={`cz${closing ? ' closing' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button className="modal-x" onClick={close} aria-label="Cerrar">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>

        <div className="cz-steps">
          {steps.map((s, i) => (
            <div key={s} className={`cz-step ${i === step ? 'on' : ''} ${i < step ? 'done' : ''}`}>
              <span className="cz-num">{i < step ? <Ico.Check width="13" height="13" /> : i + 1}</span>
              <span className="cz-lab">{s}</span>
            </div>
          ))}
          <div className="cz-progress"><span style={{ width: (step / 3 * 100) + '%' }}></span></div>
        </div>

        <div className="cz-body">
          {step === 0 && (
            <div className="cz-pane">
              <h4>Tu auto</h4>
              <p className="cz-sub">Esto es lo que armaste. Si algo cambió, ciérralo y ajústalo arriba.</p>
              <div className="cz-summary">
                <div><span>Auto</span><b>{state.marca} {state.modelo} {state.version}</b></div>
                <div><span>Año</span><b>{state.year}{state.condicion === 'seminuevo' ? ' · Seminuevo' : ''}</b></div>
                <div><span>Tipo</span><b>{cap(state.carroceria)} · {cap(state.motor)}</b></div>
                <div><span>Precio</span><b>{fmtMXN(state.precio)}</b></div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="cz-pane">
              <h4>Tu plan</h4>
              <p className="cz-sub">Arrendamiento {state.plan === 'puro' ? 'Puro (deducible)' : 'Financiero'}.</p>
              <div className="cz-summary">
                <div><span>Plan</span><b>{planLabel(state.plan)}</b></div>
                <div><span>Plazo</span><b>{state.plazo} meses</b></div>
                <div><span>Pago inicial</span><b>{fmtMXN(state.enganche)}</b></div>
                <div className="hl"><span>Renta mensual</span><b>{busy ? 'Actualizando…' : `${fmtMXN(monthly)}/mes`}</b></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="cz-pane">
              <h4>Elige tu seguro</h4>
              <p className="cz-sub">Va incluido en tu renta mensual. Sin trámites extra.</p>
              <div className="ins-grid">
                {real ? real.options.map((o, i) => (
                  <button key={o.logo} className={`ins-card ${seguro === o.logo ? 'on' : ''}`} disabled={busy}
                    onClick={() => { if (o.logo !== seguro) { setSeguro(o.logo); real.setInsurance(o.logo); } }}>
                    <span className="ins-name">{o.name}</span>
                    <span className="ins-note">{i === 0 ? 'Mejor precio · ' : ''}Prima {fmtMXN(o.amount)}</span>
                    {seguro === o.logo && <span className="ins-ck"><Ico.Check width="13" height="13" /></span>}
                  </button>
                )) : SEGUROS.map(o => (
                  <button key={o.id} className={`ins-card ${seguro === o.id ? 'on' : ''}`} onClick={() => setSeguro(o.id)}>
                    <span className="ins-name">{o.name}</span>
                    <span className="ins-note">{o.note}</span>
                    {seguro === o.id && <span className="ins-ck"><Ico.Check width="13" height="13" /></span>}
                  </button>
                ))}
              </div>
              {busy && <p className="cz-sub cz-updating">Actualizando tu renta con este seguro…</p>}
            </div>
          )}

          {step === 3 && (
            <div className="cz-pane">
              <h4>Tus datos</h4>
              <p className="cz-sub">Para preparar tu solicitud y contactarte. Toma 1 minuto.</p>
              <div className="cz-form">
                <label>Nombre completo
                  <input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="Ej. María López" />
                </label>
                <label>Correo
                  <input type="email" inputMode="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="tu@correo.com" className={form.email && !emailOk ? 'cz-invalid' : ''} />
                  {form.email && !emailOk && <span className="cz-hint">Escribe un correo válido (ej. tu@correo.com).</span>}
                </label>
                <label>WhatsApp / teléfono
                  <input type="tel" inputMode="numeric" value={form.tel} onChange={(e) => setForm({ ...form, tel: e.target.value })} placeholder="55 1234 5678" className={form.tel && !telOk ? 'cz-invalid' : ''} />
                  {form.tel && !telOk && <span className="cz-hint">Debe tener 10 dígitos.</span>}
                </label>
              </div>
              {submitError && <p className="cz-err">{submitError}</p>}
            </div>
          )}
        </div>

        <div className="cz-nav">
          {step > 0 ? <button className="cz-back" onClick={() => setStep(step - 1)}>Atrás</button> : <span />}
          <button className="cgo" disabled={!canNext} onClick={next}>
            {step === 3 ? (sending ? 'Enviando…' : 'Enviar solicitud') : 'Continuar'} <Ico.Arrow />
          </button>
        </div>

        <p className="cz-disclaimer">Cotización de ejemplo · la cotización real puede cambiar.</p>

        <div className="modal-foot">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" /></svg>
          Tus datos están protegidos. Consulta nuestro <a href="/aviso-de-privacidad">aviso de privacidad</a>.
        </div>
      </div>
    </div>
  );
}

export default Cotizador;
